import React, { useState } from 'react';
import {
  Home,
  Users,
  Building2,
  GraduationCap,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  UserCheck,
  MapPin,
  Calendar,
  Bell,
  BookOpen,
  FileCheck,
  Target,
  TrendingUp,
  MessageSquare,
  Phone,
  Headphones,
  Euro,
  Calculator,
  Clock,
  Plane,
  BookUser,
  CreditCard,
  Award,
  Globe,
  Send,
  Mail,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Plus,
  Stethoscope,
  Check
} from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getUnitTypeName } from '../../data/mockData';
import AddLeadModal from '../Modals/AddLeadModal';
import SupportModal from '../Modals/SupportModal';
import MissedCallsModal from '../Karel/MissedCallsModal';
import { useLeads } from '../../hooks/useSupabase';
import { supabase } from '../../lib/supabase';


interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange }) => {
  const { user, logout, savedSessions, switchAccount, departmentProfiles, switchDepartmentProfile, addAccount } = useAuth();
  const { fetchLeads } = useLeads();
  const [showAddLeadModal, setShowAddLeadModal] = React.useState(false);
  const [showMissedCallsModal, setShowMissedCallsModal] = React.useState(false);
  const [showSupportModal, setShowSupportModal] = React.useState(false);
  const [missedCalls, setMissedCalls] = React.useState<any[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  React.useEffect(() => {
    fetchLeads();
  }, []);

  React.useEffect(() => {
    if (['sms-sender', 'newsletter'].includes(activeTab)) {
      setOpenSubmenuId('marketing');
    }
  }, [activeTab]);

  /*
  const userDeadlineNotifications = mockDeadlineNotifications.filter(
    (notif) => notif.userId === user?.id && !notif.isRead
  );
  */

  // const totalLeads = leads?.length || 0;

  React.useEffect(() => {
    const handleMissedCall = (event: any) => {
      const callData = event.detail;
      if (callData.direction === 'incoming' && callData.status === 'missed') {
        setMissedCalls(prev => {
          const newCall = {
            id: callData.callId || Date.now(),
            number: callData.number || callData.phoneNumber,
            name: callData.name || 'Bilinmeyen',
            time: new Date(callData.time || Date.now()),
            duration: 0,
            status: 'missed'
          };
          return [newCall, ...prev.slice(0, 49)];
        });
      }
    };

    const handleCallAnswered = (event: any) => {
      const callData = event.detail;
      setMissedCalls(prev => prev.filter(call =>
        call.number !== (callData.number || callData.phoneNumber)
      ));
    };

    window.addEventListener('karelMissedCall', handleMissedCall);
    window.addEventListener('karelAnswered', handleCallAnswered);
    window.addEventListener('karelHangup', handleMissedCall);

    return () => {
      window.removeEventListener('karelMissedCall', handleMissedCall);
      window.removeEventListener('karelAnswered', handleCallAnswered);
      window.removeEventListener('karelHangup', handleMissedCall);
    };
  }, []);

  // Chat Notifications & Unread Count
  React.useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = async () => {
      try {
        const { supabase } = await import('../../lib/supabase'); // Keep dynamic if preferred, or switch to static. 
        // NOTE: The user's code uses dynamic imports heavily in this file for some reason, 
        // but for consistency/performance I'll keep it but clean up the nested logic.
        // Actually, let's just use the supabase instance we likely can get from context or import at top?
        // Use the one from dynamic import as per existing pattern to avoid large refactors

        const { data, error } = await supabase.rpc('get_unread_message_count');

        if (!error && typeof data === 'number') {
          setUnreadCount(data);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();

    let messageSubscription: any;
    let participantSubscription: any;

    const setupSubscriptions = async () => {
      const { supabase } = await import('../../lib/supabase');

      // Listen for new messages
      messageSubscription = supabase
        .channel('public:chat_messages:sidebar')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages'
        }, async (payload) => {
          const newMsg = payload.new as any;
          if (newMsg.sender_id !== user.id) {
            // Simply re-fetch the count to be accurate
            fetchUnreadCount();

            // Check details for Toast notification
            const { data: participation } = await supabase
              .from('chat_participants')
              .select('user_id')
              .eq('conversation_id', newMsg.conversation_id)
              .eq('user_id', user.id)
              .single();

            if (participation) {
              // Get sender name for toast
              const { data: sender } = await supabase
                .from('profiles')
                .select('name')
                .eq('id', newMsg.sender_id)
                .single();

              toast(
                (t) => (
                  <div onClick={() => {
                    onTabChange('messages');
                    toast.dismiss(t.id);
                  }} className="cursor-pointer">
                    <p className="font-bold">{sender?.name || 'Yeni Mesaj'}</p>
                    <p className="text-sm truncate max-w-[200px]">{newMsg.content || 'Dosya gönderildi'}</p>
                  </div>
                ),
                {
                  duration: 4000,
                  position: 'top-right',
                  icon: '💬',
                  style: {
                    background: '#fff',
                    color: '#333',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  },
                }
              );
            }
          }
        })
        .subscribe();

      // Listen for read status updates (e.g. when I read a message)
      participantSubscription = supabase
        .channel('public:chat_participants:sidebar')
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_participants',
          filter: `user_id=eq.${user.id}`
        }, () => {
          // When our read status updates, re-fetch count
          fetchUnreadCount();
        })
        .subscribe();
    };

    setupSubscriptions();

    return () => {
      import('../../lib/supabase').then(({ supabase }) => {
        if (messageSubscription) supabase.removeChannel(messageSubscription);
        if (participantSubscription) supabase.removeChannel(participantSubscription);
      });
    };
  }, [user]);

  const handleSaveLead = async (leadPayload: any) => {
    try {
      const { supabase } = await import('../../lib/supabase');

      const { data, error } = await supabase
        .from('leads')
        .insert(leadPayload)
        .select()
        .single();

      if (error) {
        console.error('Lead kaydetme hatası:', error);
        return { ok: false, error: error.message };
      }

      console.log('Lead başarıyla kaydedildi:', data);
      return { ok: true, data };
    } catch (err) {
      console.error('Beklenmeyen hata:', err);
      return { ok: false, error: 'Beklenmeyen bir hata oluştu' };
    }
  };

  const getMenuItems = () => {
    // Determine specific dashboard ID based on unit type
    let dashboardId = 'dashboard';
    if (user?.unitType === 'sales') dashboardId = 'sales';
    if (user?.unitType === 'accounting') dashboardId = 'accounting';

    const baseItems = user?.unitType === 'visa_consultant' || user?.unitType === 'visa_consultant' || user?.unitType === 'testprep' || user?.unitType === 'education_consultant'
      ? []
      : [
        { id: dashboardId, label: 'Dashboard', icon: Home },
        { id: 'deadlines', label: 'Deadline', icon: Clock },
      ];

    const messageItem = {
      id: 'messages',
      label: 'Mesajlar',
      icon: MessageSquare,
      badge: unreadCount
    };

    if (user?.role === 'admin') {
      return [
        ...baseItems,
        { id: 'students', label: 'Tüm Öğrenciler', icon: GraduationCap },
        { id: 'branches', label: 'Şubeler', icon: Building2 },
        { id: 'users', label: 'Kullanıcılar', icon: UserCheck },
        { id: 'sms-sender', label: 'SMS Paneli', icon: Send },
        messageItem,
        { id: 'settings', label: 'Ayarlar', icon: Settings },
      ];
    }

    if (user?.role === 'branch') {
      return [
        ...baseItems,
        { id: 'students', label: 'Öğrenciler', icon: GraduationCap },
        { id: 'applications', label: 'Başvurular', icon: FileText },
        { id: 'units', label: 'Birimler', icon: MapPin },
        { id: 'reports', label: 'Raporlar', icon: BarChart3 },
        messageItem,
      ];
    }

    if (user?.unitType === 'accounting') {
      return [
        ...baseItems,
        { id: 'contracts', label: 'Sözleşmeler', icon: FileText },
        { id: 'installments', label: 'Taksit Takibi', icon: CreditCard },
        { id: 'expenses', label: 'Giderler', icon: Euro },
        { id: 'leave-tracking', label: 'İzin Takip', icon: Calendar },
        { id: 'financial-reports', label: 'Mali Raporlar', icon: Calculator },
        messageItem,
        { id: 'settings', label: 'Ayarlar', icon: Settings },
      ];
    }

    if (user?.role === 'unit') {
      const unitSpecificItems: any[] = [];

      if (user.unitType === 'education_consultant') {
        unitSpecificItems.push(
          { id: 'operations-dashboard', label: 'Dashboard', icon: Home },
          { id: 'operations-leads', label: 'Muhasebeden Gelenler', icon: Target },
          {
            id: 'engineering',
            label: 'Mühendislik',
            icon: Calculator,
            subItems: [
              { id: 'all-engineering', label: 'Tüm Mühendislikler', icon: Calculator },
              { id: 'polito', label: 'Polito', icon: Calculator },
              { id: 'polimi', label: 'Polimi', icon: Calculator },
            ]
          },
          { id: 'imat', label: 'IMAT', icon: FileCheck },
          {
            id: 'tip',
            label: 'TIP',
            icon: Stethoscope,
            subItems: [
              { id: 'all-medical', label: 'Tüm Tıp', icon: Stethoscope },
              { id: 'san-raffaele', label: 'San Raffaele', icon: Stethoscope },
              { id: 'humanitas', label: 'Humanitas', icon: Stethoscope },
              { id: 'cattolica', label: 'Cattolica', icon: Stethoscope },
              { id: 'biomedico', label: 'Biomedico', icon: Stethoscope },
              { id: 'unicamillus', label: 'Unicamillus', icon: Stethoscope },
            ]
          },
          { id: 'license', label: 'Lisans', icon: BookOpen },
          { id: 'architecture', label: 'Mimarlık', icon: Building2 },
          { id: 'students', label: 'Öğrencilerim', icon: GraduationCap },
          { id: 'exams', label: 'Sınavlar', icon: FileCheck },
          { id: 'universities', label: 'Üniversiteler', icon: Building2 },
          { id: 'preferences', label: 'Tercihler', icon: Award },
          { id: 'tasks', label: 'Görevlerim', icon: FileCheck },
          messageItem,
          { id: 'settings', label: 'Ayarlar', icon: Settings },
        );
      }

      if (user.unitType === 'visa' || user.unitType === 'visa_consultant') {
        unitSpecificItems.push(
          { id: 'visa', label: 'Dashboard', icon: Home },
          { id: 'deadlines', label: 'Deadline', icon: Clock },
          { id: 'visa-appointments', label: 'Randevu Kayıtları', icon: Calendar },
          { id: 'visa-applications', label: 'Vize Kayıtları', icon: Plane },
          { id: 'visa-documents', label: 'Belge Yönetimi', icon: FileCheck },
          { id: 'visa-checklists', label: 'Kontrol Listeleri', icon: FileText },
          { id: 'sms-sender', label: 'SMS Gönder', icon: Send },
          messageItem,
          { id: 'settings', label: 'Ayarlar', icon: Settings },
        );
      }

      if (user.unitType === 'marketing') {
        unitSpecificItems.push(
          { id: 'leads', label: 'Potansiyel Müşteriler', icon: Target },
          { id: 'campaigns', label: 'Kampanyalar', icon: TrendingUp },
          { id: 'analytics', label: 'Analitik', icon: BarChart3 },
          { id: 'content', label: 'İçerik Yönetimi', icon: FileText },
          messageItem,
        );
      }

      if (user.unitType === 'sales') {
        unitSpecificItems.push(
          { id: 'sales-leads', label: "Lead'ler", icon: Target },
          { id: 'serious-candidates', label: 'Ciddi Adaylar', icon: Award },
          { id: 'follow-ups', label: 'Follow Up\'lar', icon: Calendar },
          { id: 'future-registrations', label: 'Kayıtlar', icon: Calendar },
          { id: 'contracts', label: 'Sözleşmeler', icon: FileText },
          { id: 'fair-data', label: 'Fuar Dataları', icon: Plane },
          { id: 'website-data', label: 'Website Dataları', icon: Globe },
          { id: 'sales-reports', label: 'Satış Raporları', icon: BarChart3 },
          {
            id: 'marketing',
            label: 'Pazarlama',
            icon: TrendingUp,
            subItems: [
              { id: 'sms-sender', label: 'SMS Gönder', icon: Send },
              { id: 'newsletter', label: 'Newsletter', icon: Mail },
            ]
          },
          { id: 'calendar', label: 'Takvim', icon: Calendar },
          { id: 'contacts', label: 'Rehber', icon: BookUser },

          { id: 'gmail', label: 'Gmail', icon: MessageSquare },
          messageItem,
          { id: 'settings', label: 'Ayarlar', icon: Settings }
        );
      }

      if (user.unitType === 'testprep') {
        unitSpecificItems.push(
          { id: 'testprep-dashboard', label: 'Dashboard', icon: Home },
          { id: 'testprep-leads', label: "Lead'ler", icon: Target },
          { id: 'revenue', label: 'Gelir Takibi', icon: TrendingUp },
          { id: 'mock-exams', label: 'Deneme Sınavları', icon: FileCheck },
          { id: 'course-enrollments', label: 'Kurs Kayıtları', icon: BookOpen },
          { id: 'payments', label: 'Ödemeler', icon: CreditCard },
          { id: 'teacher-payments', label: 'Hoca Ödemeleri', icon: Euro },
          { id: 'private-lessons', label: 'Özel Dersler', icon: GraduationCap },
          { id: 'coaching', label: 'Öğrenci Koçluğu', icon: Target },
          { id: 'document-services', label: 'Doküman Hizmetleri', icon: FileText },
          { id: 'students', label: 'TestPrep Öğrencileri', icon: Users },
          { id: 'instructors', label: 'Eğitmenler', icon: GraduationCap },
          { id: 'teacher-responses', label: 'Öğretmen Yanıtları', icon: FileCheck },
          { id: 'sms-sender', label: 'SMS Gönder', icon: Send },
          messageItem,
          { id: 'settings', label: 'Ayarlar', icon: Settings },
        );
      }

      if (user.unitType === 'customer_service') {
        unitSpecificItems.push(
          { id: 'tickets', label: 'Destek Talepleri', icon: MessageSquare },
          { id: 'customers', label: 'Müşteriler', icon: Users },
          { id: 'calls', label: 'Çağrı Merkezi', icon: Phone },
          { id: 'feedback', label: 'Geri Bildirimler', icon: Bell },
          messageItem,
        );
      }

      if (user.unitType === 'teknik_destek') {
        unitSpecificItems.push(
          { id: 'tech-support-dashboard', label: 'Genel Bakış', icon: Home },
          { id: 'support-tickets', label: 'Destek Talepleri', icon: MessageSquare },
          { id: 'user-management', label: 'Kullanıcı Yönetimi', icon: Users },
          messageItem,
          { id: 'settings', label: 'Ayarlar', icon: Settings },
        );
      }

      /* Removed duplicate accounting block which was causing issues */

      return [
        ...baseItems,
        ...unitSpecificItems,
      ];
    }

    return [...baseItems, messageItem];
  };

  const menuItems = getMenuItems();

  const getUserRoleText = () => {
    if (user?.role === 'admin') return 'Sistem Yöneticisi';
    if (user?.role === 'branch') return 'Şube Yöneticisi';
    if (user?.unitType === 'accounting') return 'Muhasebe';
    if (user?.role === 'unit' && user?.unitType) return getUnitTypeName(user.unitType);
    return 'Kullanıcı';
  };

  const initials =
    user?.name
      ? user.name
        .split(' ')
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
      : 'U';

  return (
    <>
      <div className={`bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col h-full transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-72'} ${user?.unitType === 'education_consultant' ? 'operations-sidebar' : ''} relative`}>
        {/* Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1 shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-50 text-gray-500"
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        {/* User Info */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 relative">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors ${isProfileMenuOpen ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
          >
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user?.name || 'User'}
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center shrink-0">
                <span className="text-white text-sm font-semibold">{initials}</span>
              </div>
            )}
            {!isCollapsed && (
              <div className="flex-1 min-w-0 transition-opacity duration-300">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  {getUserRoleText()}
                  <ChevronDown className={`w-3 h-3 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                </p>
              </div>
            )}
          </div>

          {/* Account Switching Dropdown */}
          <div className={`${isProfileMenuOpen ? 'block' : 'hidden'} absolute left-4 right-4 top-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden`}>
            {/* 1. Department Accounts for current logged-in email */}
            {departmentProfiles.length > 0 && (
              <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-2">Departman Hesaplarınız</span>
                <div className="space-y-1 mt-1 max-h-48 overflow-y-auto">
                  {departmentProfiles.map((profile) => {
                    const isActive = profile.id === user?.id;
                    const formatUnitName = (type?: string, role?: string): string => {
                      if (role === 'admin') return 'Sistem Yöneticisi';
                      if (role === 'branch') return 'Şube Yöneticisi';
                      switch (type) {
                        case 'education_consultant': return 'Eğitim Danışmanı';
                        case 'visa_consultant': return 'Vize Danışmanı';
                        case 'sales': return 'Satış Birimi';
                        case 'marketing': return 'Pazarlama Birimi';
                        case 'accounting': return 'Muhasebe Birimi';
                        case 'customer_service': return 'Müşteri Hizmetleri';
                        case 'testprep': return 'Test Hazırlık Birimi';
                        case 'teknik_destek': return 'Teknik Destek';
                        default: return role === 'unit' ? 'Birim Çalışanı' : (role || 'Departman');
                      }
                    };

                    return (
                      <button
                        key={profile.id}
                        onClick={() => {
                          switchDepartmentProfile(profile.id);
                          setIsProfileMenuOpen(false);
                        }}
                        className={`w-full flex items-center p-2 rounded-lg text-left transition-colors ${
                          isActive
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-2 text-xs shrink-0 ${
                          isActive ? 'bg-blue-600 text-white font-bold' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                        }`}>
                          {profile.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{profile.name}</p>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                            {formatUnitName(profile.unitType, profile.role)}
                          </p>
                        </div>
                        {isActive && (
                          <Check className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 ml-1" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 2. Other Saved Sessions (Different Emails) */}
            {savedSessions.filter(s => !departmentProfiles.some(dp => dp.id === s.user.id)).length > 0 && (
              <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-2">Diğer Hesaplar</span>
                <div className="space-y-1 mt-1 max-h-36 overflow-y-auto">
                  {savedSessions.filter(s => !departmentProfiles.some(dp => dp.id === s.user.id)).map((session) => (
                    <button
                      key={session.user.id}
                      onClick={() => {
                        switchAccount(session.user.id);
                        setIsProfileMenuOpen(false);
                      }}
                      className="w-full flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left rounded-lg"
                    >
                      {session.user.avatar ? (
                        <img src={session.user.avatar} className="w-7 h-7 rounded-full border border-gray-200 dark:border-gray-600 mr-2" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-2 text-xs font-bold text-gray-500">
                          {session.user.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{session.user.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{session.user.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="p-1">
              <button
                onClick={() => addAccount()}
                className="w-full flex items-center px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors rounded-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Farklı E-posta ile Giriş Yap / Hesap Ekle
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats & Karel Widget */}
        {user?.role !== 'student' && !isCollapsed && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="space-y-3">
              {/* Cevapsiz Cagrilar */}
              {/* Disable Karel Missed Calls button for now
              <button
                onClick={() => setShowMissedCallsModal(true)}
                className="w-full flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-red-900 dark:text-red-300">Cevapsız Çağrılar</p>
                    <p className="text-xs text-red-700 dark:text-red-400">Karel sisteminden</p>
                  </div>
                </div>
                {missedCalls.length > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px] text-center">
                    {missedCalls.length}
                  </span>
                )}
              </button>
              */}
            </div>
          </div>
        )}

        {/* Disable Karel Collapsed Icons for now
        {user?.role !== 'student' && isCollapsed && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex flex-col items-center gap-4">
            <button
              onClick={() => setShowMissedCallsModal(true)}
              className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center relative hover:bg-red-200 transition-colors"
              title="Cevapsız Çağrılar"
            >
              <Phone className="w-5 h-5 text-red-600 dark:text-red-400" />
              {missedCalls.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full">
                  {missedCalls.length}
                </span>
              )}
            </button>
          </div>
        )}
        */}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto overflow-x-hidden">
          {menuItems.map((item: any) => {
            const Icon = item.icon;

            // Handle Items with Submenu
            if (item.subItems) {
              const isSubmenuActive = item.subItems.some((sub: any) => sub.id === activeTab);
              const showSubmenu = openSubmenuId === item.id;

              return (
                <div key={item.id} className="space-y-1">
                  <button
                    onClick={() => {
                      if (isCollapsed) {
                        setIsCollapsed(false);
                        setOpenSubmenuId(item.id);
                      } else {
                        setOpenSubmenuId(showSubmenu ? null : item.id);
                      }
                    }}
                    className={`flex items-center px-3 py-2.5 rounded-lg text-left transition-all duration-200 w-full ${isSubmenuActive
                      ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                      } ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <Icon className={`min-w-[1.25rem] w-5 h-5 ${isSubmenuActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                    {!isCollapsed && (
                      <div className="flex items-center justify-between flex-1 truncate">
                        <span className="font-medium truncate">{item.label}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showSubmenu ? 'rotate-180' : ''}`} />
                      </div>
                    )}
                  </button>

                  {/* Submenu Items */}
                  {!isCollapsed && showSubmenu && (
                    <div className="pl-9 space-y-1">
                      {item.subItems.map((subItem: any) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = activeTab === subItem.id;
                        return (
                          <button
                            key={subItem.id}
                            onClick={() => onTabChange(subItem.id)}
                            className={`flex items-center px-3 py-2 rounded-lg text-left transition-all duration-200 w-full text-sm ${isSubActive
                              ? 'text-blue-600 bg-blue-50/50 dark:text-blue-400 dark:bg-blue-900/10'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
                              } space-x-3`}
                          >
                            <SubIcon className="w-4 h-4" />
                            <span className="truncate">{subItem.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => {
                  console.log('Sidebar menu clicked:', item.id, item.label);
                  onTabChange(item.id);
                }}
                className={`flex items-center px-3 py-2.5 rounded-lg text-left transition-all duration-200 w-full ${isActive
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                  } ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon
                  className={`min-w-[1.25rem] w-5 h-5 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                    }`}
                />
                {!isCollapsed && (
                  <div className="flex items-center justify-between flex-1 truncate">
                    <span className="font-medium truncate">{item.label}</span>
                    {item.badge && item.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px] text-center ml-2">
                        {item.badge}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Quick Actions */}
        {/* Quick Actions - Hide for Visa Dept */}
        {user?.unitType === 'sales' && !isCollapsed && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={() => setShowAddLeadModal(true)}
              className="w-full flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors text-sm justify-center"
            >
              <Users className="w-4 h-4" />
              <span>Yeni Öğrenci</span>
            </button>
          </div>
        )}

        {user?.unitType === 'sales' && isCollapsed && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex justify-center">
            <button
              onClick={() => setShowAddLeadModal(true)}
              className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center transition-colors"
              title="Yeni Öğrenci"
            >
              <Users className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Destek Hattı */}
        {!isCollapsed && (
          <div className="p-4">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
              <h4 className="font-semibold mb-1">Destek Hattı</h4>
              <p className="text-xs text-green-100 mb-3">Sorun yaşarsanız bize ulaşın.</p>
              <button
                onClick={() => setShowSupportModal(true)}
                className="w-full flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm py-2 px-3 rounded-lg text-sm font-medium transition-colors"
              >
                <Headphones className="w-4 h-4" />
                Yardım İste
              </button>
            </div>
          </div>
        )}

        {isCollapsed && (
          <div className="p-4 flex justify-center">
            <button
              onClick={() => setShowSupportModal(true)}
              className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center transition-colors"
              title="Destek Hattı"
            >
              <Headphones className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Logout */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={() => {
              if (window.stopKarelWebPhone) {
                window.stopKarelWebPhone();
              }
              logout();
            }}
            className={`w-full flex items-center px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
            title={isCollapsed ? "Çıkış Yap" : undefined}
          >
            <LogOut className="w-5 h-5" />
            {!isCollapsed && <span className="font-medium">Çıkış Yap</span>}
          </button>
        </div>
      </div>

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onSave={handleSaveLead}
        token={(user as any)?.webphoneToken}
        onSaved={(created) => {
          console.log('Lead basariyla olusturuldu:', created);
          setShowAddLeadModal(false);
          fetchLeads();
        }}
      />
      {/* Missed Calls Modal */}
      <MissedCallsModal
        isOpen={showMissedCallsModal}
        onClose={() => setShowMissedCallsModal(false)}
      />

      <Toaster />

      {/* Support Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </>
  );
};

export default Sidebar;
